import { useCallback } from "react"
import { PanelModes, PanelNames } from "panels/constants"
import { usePanelsState, usePanelState } from "panels/state"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    setTitle as setTitleAction,
    setDescription as setDescriptionAction,
    setContentComponent as setContentComponentAction,
    setType as setTypeAction,
    queueComponent as queueComponentAction,
    removedQueuedComponent as removedQueuedComponentAction,
    activatedOverlayAction,
    resetOverlayAction,
    hiddenOverlayAction,
} from "./slice"
import {
    selectNextComponentFromQueue,
    selectOverlayState,
    selectOverlayComponentName,
} from "./selectors"
import { ModalContainerComponent } from "features/modals"
import store from "integrations/redux/store"
import { suspendCurrentlyVisiblePanels } from "panels/state/usePanelsState"
import { OverlayModalType } from "themes/default-tailwind"

type ContentInOverlay = {
    componentName: string
    hidePanels?: boolean
    modalType?: OverlayModalType
}

export const showContentInOverlay = ({
    componentName,
    hidePanels = true,
    modalType,
}: ContentInOverlay) => {
    const dispatch = store.dispatch

    dispatch(setContentComponentAction(componentName))

    if (hidePanels) {
        suspendCurrentlyVisiblePanels()
    }
    if (modalType) {
        dispatch(setTypeAction(modalType))
    }

    dispatch(activatedOverlayAction())
}

export const useOverlayState = () => {
    const dispatch = useAppDispatch()
    const { suspendPanels } = usePanelsState()
    const { mode, setMode, restoreSuspendedPanels } = usePanelState(PanelNames.Overlay)
    const nextComponent = useAppSelector(selectNextComponentFromQueue)
    const contentComponentName = useAppSelector(selectOverlayComponentName)

    const overlayState = useAppSelector(selectOverlayState)
    const { title, description } = overlayState || {}

    const setTitle = useCallback(
        (title: string) => {
            dispatch(setTitleAction(title))
        },
        [dispatch]
    )

    const setDescription = useCallback(
        (description: string) => {
            dispatch(setDescriptionAction(description))
        },
        [dispatch]
    )

    const setContent = useCallback(
        (contentComponent?: ModalContainerComponent) => {
            dispatch(setContentComponentAction(contentComponent?.mappedName))
        },
        [dispatch]
    )

    const setType = useCallback(
        (type: OverlayModalType) => {
            dispatch(setTypeAction(type))
        },
        [dispatch]
    )

    const showContent = useCallback(
        (
            contentComponent: ModalContainerComponent,
            type = OverlayModalType.General,
            hidePanels = true
        ) => {
            setType(type)
            showContentInOverlay({ componentName: contentComponent.mappedName, hidePanels })
        },
        [setType]
    )

    const hideOverlay = useCallback(() => {
        restoreSuspendedPanels()
        dispatch(hiddenOverlayAction())
    }, [dispatch, restoreSuspendedPanels])

    const nextOrHide = useCallback(() => {
        if (nextComponent) {
            dispatch(setContentComponentAction(nextComponent))
            dispatch(removedQueuedComponentAction())
        } else {
            hideOverlay()
        }
    }, [dispatch, nextComponent, hideOverlay])

    const showOverlay = useCallback(
        (hidePanels = true) => {
            if (!contentComponentName) {
                return
            }
            if (hidePanels) suspendPanels()
            setMode(PanelModes.Regular)
        },
        [suspendPanels, setMode, contentComponentName]
    )

    const queueComponent = useCallback(
        (contentComponent: ModalContainerComponent) => {
            dispatch(queueComponentAction(contentComponent.mappedName))
        },
        [dispatch]
    )

    const resetOverlay = useCallback(() => {
        restoreSuspendedPanels()
        dispatch(resetOverlayAction())
    }, [dispatch, restoreSuspendedPanels])

    return {
        isVisible: mode !== PanelModes.Hidden,
        title,
        description,
        setTitle,
        setDescription,
        setContent,
        setType,
        showContent,
        hideOverlay,
        showOverlay,
        resetOverlay,
        dispatch,
        queueComponent,
        nextOrHide,
    }
}

export default useOverlayState
